<template>
  <ValidationObserver
    ref="mergeDocumentObserver"
    tag="section"
    :class="{ 'spacing-px-20': !isMobile }"
  >
    <b-loading isFullPage :active="isLoading" />
    <TimelineItem
      :title="General.TEXTS.JOINT_BY_DOCUMENT"
      stepValue="1"
      statusSize="large"
      :statusColor="`${getModifiedJointFiles.length ? 'primary' : 'default'}`"
    >
      <b-field
        data-cy="FILE_JOINT"
        :label="`${General.DOCUMENT}s`"
        class="collapse-custom spacing-py-15"
        :class="{
          invalidatedField: !isContrast && invalidDocumentsError.length
        }"
        customClass="has-text-color-7"
        :type="
          isContrast
            ? 'is-white'
            : invalidDocumentsError.length
            ? 'is-refused'
            : ''
        "
      >
        <div>
          <div
            class="overflow-scroll-Y"
            :class="{ 'spacing-pr-15': !isMobile }"
          >
            <ListAttachments
              v-model="currentPageNoEditable"
              isJoint
              :onEdit="editFile"
              :showModalUpload="showModalUpload"
              :processNup="nup"
            />
          </div>
        </div>
        <template #message>
          <ErrorField :errors="invalidDocumentsError" />
        </template>
      </b-field>
    </TimelineItem>
    <TimelineItem
      :title="General.JUSTIFICATION"
      stepValue="2"
      statusSize="large"
      :statusColor="`${getCurrentJoint.motivation ? 'primary' : 'default'}`"
    >
      <ValidationProvider v-slot="{ errors }" rules="required|max:200">
        <b-field
          customClass="has-text-color-7"
          :type="isContrast ? 'is-white' : errors.length ? 'is-refused' : ''"
        >
          <b-input
            :value="getCurrentJoint.motivation"
            :placeholder="General.PLACEHOLDER.JOINT_MOTIVATION"
            type="textarea"
            role="input"
            @input="setMotivation"
            @blur="setProcessJoint"
          />
          <template #message>
            <ErrorField :errors="errors" />
          </template>
        </b-field>
      </ValidationProvider>
    </TimelineItem>
    <div class="custom-buttons">
      <b-tooltip
        :active="hasUploadFiles"
        :label="General.TEXTS.WAIT_FOR_ALL_FILES_TO_LOAD"
        :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
        position="is-right"
        role="tooltip"
      >
        <b-button
          :type="isContrast ? 'is-white' : 'is-green-lawn'"
          role="button"
          :disabled="hasUploadFiles"
          @click="submitJoint"
        >
          <span>
            {{ General.SAVE }}
          </span>
        </b-button>
      </b-tooltip>
      <b-button
        data-cy="DELETE_JOINT_DRAFT_BUTTON"
        :type="isContrast ? 'is-white' : 'is-orange'"
        outlined
        role="button"
        @click="cancelJoint"
      >
        <span>
          {{ General.CANCEL }}
        </span>
      </b-button>
    </div>
  </ValidationObserver>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";

import ModalAttachments from "@/views/App/ModalAttachments";
import ModalWarning from "@/views/App/ModalWarning.vue";
import { General } from "@/utils/constants";
import {
  PROCESS_DETAILS_TABS as TABS,
  ATTACHEMENT_MAX_COUNT
} from "@/utils/enumerate";
import ListAttachments from "@/components/ListAttachments";
import TimelineItem from "@/components/TimelineItem";
import { validate } from "vee-validate";

export default {
  name: "MergeDocumentTab",
  components: {
    ListAttachments,
    TimelineItem
  },

  props: {
    isUserResponsable: {
      type: Boolean,
      default: false
    },
    nup: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      General,
      TABS,
      isLoading: false,
      mergeType: "document",
      invalidDocumentsError: [],
      currentPageNoEditable: 1
    };
  },

  computed: {
    ...mapGetters({
      getCurrentJoint: "getCurrentJoint",
      getModifiedJointFiles: "getModifiedJointFiles",
      user: "getCurrentUser",
      getCurrentTab: "getCurrentTabProcessDetails",
      getDraftFilesSize: "getDraftFilesSize",
      getProcessFileSize: "getProcessFileSize",
      needUpdateAttachments: "needUpdateAttachments",
      isContrast: "isContrast",
      isMobile: "isMobile",
      getListUploadFiles: "getListUploadFiles"
    }),

    getCurrentSizeProcess() {
      return this.getDraftFilesSize("jointFiles") + this.getProcessFileSize;
    },

    activeTab() {
      return this.getCurrentTab.value;
    },

    isJointDocumentTab() {
      return this.activeTab === this.TABS.MERGE_DOCUMENTS.value;
    },

    hasUploadFiles() {
      return this.getListUploadFiles.some(
        file =>
          file.document_join_id === this.getCurrentJoint.id &&
          !file.isErro &&
          file.status === "pending"
      );
    }
  },

  watch: {
    async currentPageNoEditable(newPage) {
      this.isLoading = true;
      await this.getJointFile({
        jointId: this.getCurrentJoint.id,
        page: newPage,
        paginator_count: ATTACHEMENT_MAX_COUNT
      });
      this.isLoading = false;
    },

    getModifiedJointFiles() {
      if (this.invalidDocumentsError.length) this.checkValidJoint();
    },

    needUpdateAttachments(value) {
      if (
        this.isJointDocumentTab &&
        value.fileObject.document_join_id &&
        value.fileObject.process_id === this.$route.params.id
      )
        this.updateListAttachmentsAction(value);
    }
  },

  async beforeMount() {
    await this.getProcessJoint(this.$route.params.id);
    this.requestProcessFileSize({ processId: this.$route.params.id });
    this.loadProcessFile();
  },

  methods: {
    ...mapMutations({
      setJoint: "setJoint",
      setCurrentTab: "setCurrentTabProcessDetails"
    }),
    ...mapActions({
      setProcessJoint: "setProcessJoint",
      createProcessJoint: "createProcessJoint",
      deleteProcessJoint: "deleteProcessJoint",
      getJointFile: "getJointFile",
      getProcessJoint: "getProcessJoint",
      requestProcessFileSize: "requestProcessFileSize",
      updateListAttachmentsAction: "updateListAttachmentsAction"
    }),

    async loadProcessFile() {
      await this.getJointFile({
        jointId: this.getCurrentJoint.id,
        page: 1,
        paginator_count: ATTACHEMENT_MAX_COUNT
      });
    },

    setMotivation(value) {
      this.setJoint({ ...this.getCurrentJoint, motivation: value });
    },

    showModalUpload() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalAttachments,
        props: {
          jointId: this.getCurrentJoint.id,
          currentSizeProcess: this.getCurrentSizeProcess,
          currentProcessId: this.getCurrentJoint.process_id
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    editFile(id, key, value, editable = false) {
      const editedFile = this.getModifiedJointFiles.find(
        file => file.id === id
      );

      if (editedFile[key] !== value) {
        editedFile[key] = value;
        this.$store.dispatch("editProcessFile", {
          gdoc: editedFile,
          editable: editable
        });
      }
    },

    checkDocStatus(acceptState) {
      return this.getModifiedJointFiles.some(
        doc => !acceptState.includes(doc.status)
      );
    },

    isValid() {
      const errors = [];

      if (!this.getCurrentJoint.motivation) errors.push(General.REASON_JOIN);
      if (!this.getModifiedJointFiles.length)
        errors.push(General.REQUIRED_ATTACH);
      if (this.checkDocStatus(["SIGNED", null]))
        errors.push(this.General.MESSAGES.SIGNING_ATTACH_FILE);

      errors.forEach(error =>
        this.snackToast.open({
          message: error,
          type: "is-danger",
          duration: 8000
        })
      );

      return !errors.length;
    },

    async checkValidJoint() {
      const isValidJustification = await this.$refs.mergeDocumentObserver.validate();

      const { valid: isValidDocuments } = await validate(
        this.getModifiedJointFiles,
        "required"
      );
      this.invalidDocumentsError = isValidDocuments
        ? []
        : [General.MESSAGES.VALIDATE.REQUIRED];

      return isValidDocuments && isValidJustification;
    },

    async submitJoint() {
      if (await this.checkValidJoint())
        if (this.isValid()) {
          this.isLoading = true;
          await this.createProcessJoint();
          this.isLoading = false;

          await this.getProcessJoint(this.$route.params.id);

          this.requestProcessFileSize({ processId: this.$route.params.id });

          this.setCurrentTab(this.TABS.DOCUMENTS.value);
        }
    },

    async refreshJoint() {
      await this.getProcessJoint(this.$route.params.id);
      this.getJointFile({
        jointId: this.getCurrentJoint.id,
        page: 1,
        paginator_count: ATTACHEMENT_MAX_COUNT
      });

      this.$refs.mergeDocumentObserver.reset();
    },

    cancelJoint() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: `${this.General.WARNING}!`,
          message: this.General.MESSAGES
            .ARE_YOU_SURE_YOU_WANT_CANCEL_JOINT_DRAFT,
          confirm: async () => {
            this.isLoading = true;
            await this.deleteProcessJoint();
            this.isLoading = false;

            this.refreshJoint();
          },
          buttons: [
            {
              label: General.YES,
              isConfirm: true,
              action: true,
              dataCY: "CONFIRM_DELETE"
            },
            {
              label: General.NO,
              isConfirm: false,
              action: false,
              dataCY: "CANCEL_DELETE"
            }
          ],
          invertOutline: true
        },
        hasModalCard: true,
        trapFocus: true
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";

.wrapper-list {
  width: 100%;
  height: 120px;
  overflow: auto;
}

.file-list {
  display: flex;
  flex-direction: column;
  margin: 10px 0 20px 0;
}

.file-list .list-columns {
  align-content: center;
  border-bottom: 1px solid $custom-color-dcdcdc;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
}

.file-list .list-columns > div:nth-child(1),
.file-list .list-columns > div:nth-child(2),
.file-list .list-columns > div:nth-child(3) {
  font-size: 0.95rem;
  font-weight: 600;
  padding: 0 5px;
}

.file-list .list-columns > div:nth-child(1),
.file-list .list-columns > div:nth-child(3) {
  width: 25%;
}

.file-list .list-columns > div:nth-child(2) {
  flex-grow: 2;
}

.file-list .list-item {
  align-content: center;
  border-bottom: 1px solid $custom-color-dcdcdc;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  padding: 5px 0;
}

.file-list .list-item > div:nth-child(1),
.file-list .list-item > div:nth-child(2),
.file-list .list-item > div:nth-child(3) {
  align-self: flex-start;
  font-size: 0.85rem;
  padding: 0 5px;
}

.file-list .list-item > div:nth-child(2) {
  font-weight: 600;
  flex-grow: 2;
}

.file-list .list-item > div:nth-child(1),
.file-list .list-item > div:nth-child(3) {
  width: 25%;
}

.custom-text-overflow {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.custom-text-overflow.text-overflow-25 {
  max-width: 25%;
}

.custom-text-overflow.text-overflow-50 {
  max-width: 50%;
}

.align-right {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
}

.custom-buttons {
  display: flex;
  margin-left: 70px;
  gap: 10px;

  @media screen and (max-width: $tablet_low) {
    margin-left: 30px;
  }
}
</style>
